// src/components/AnnouncementModal.js
// Updated: 08/27/2025 01:25 PM

import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, FormControlLabel, Checkbox, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

function AnnouncementModal({ open, onClose, onSave, announcement, editorProfile, isChurchPushNotificationsEnabled }) {
  const [title, setTitle] = useState('');
  const [richContent, setRichContent] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [sendNotification, setSendNotification] = useState(false);
  const [scheduleTime, setScheduleTime] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title);
      setRichContent(announcement.content);

      // Check if expiresAt is a valid Date object
      const expirationDate = announcement.expiresAt instanceof Date
        ? announcement.expiresAt
        : null;
      setExpiresAt(expirationDate ? expirationDate.toISOString().split('T')[0] : '');

      setExistingAttachments(announcement.attachments || []);

      // FIX: Correctly check if scheduledAt is a Date object.
      // The parent component already converts it, so we don't need to call .toDate() here.
      const scheduledDate = announcement.isScheduled && announcement.scheduledAt instanceof Date
        ? announcement.scheduledAt
        : null;
      setScheduleTime(scheduledDate);

      setSendNotification(announcement.isScheduled ? announcement.sendNotificationOnSchedule : false);
    } else {
      // Reset state for a new announcement
      setTitle('');
      setRichContent('');
      setExpiresAt('');
      setExistingAttachments([]);
      setScheduleTime(null);
      setSendNotification(false);
    }
    setSelectedFiles([]);
    setErrorMessage('');
  }, [announcement, open]);

  const handleFileChange = (e) => {
    setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
    e.target.value = null;
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (index) => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    setErrorMessage('');
    if (!title.trim() || !richContent.trim()) {
      setErrorMessage("Title and Content are required.");
      return;
    }

    const announcementData = {
      title,
      content: richContent,
      expiresAt: expiresAt ? new Date(expiresAt) : null,
      selectedFiles,
      existingAttachments,
      scheduleTime,
    };
    onSave(announcementData, sendNotification);
  };

  const canSeeNotificationCheckbox = isChurchPushNotificationsEnabled &&
                                     (editorProfile?.canSendNotifications === true ||
                                      editorProfile?.role === 'Church Administrator' ||
                                      editorProfile?.role === 'Super Administrator');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="announcement-modal-title"
        >
        <Box sx={style}>
            <Typography id="announcement-modal-title" variant="h6" component="h2">
            {announcement ? 'Edit Announcement' : 'Create New Announcement'}
            </Typography>
            {errorMessage && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errorMessage}
            </Typography>
            )}
            <TextField
            margin="normal" fullWidth label="Title" value={title}
            onChange={(e) => setTitle(e.target.value)} required
            />
            <TextField
            margin="normal" fullWidth label="Content (HTML/Markdown supported)"
            multiline rows={6} value={richContent}
            onChange={(e) => setRichContent(e.target.value)} required
            placeholder="You can paste HTML or Markdown here for rich content."
            />
            <TextField
            margin="normal" fullWidth label="Expiration Date (Optional)"
            type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ my: 2 }}>
                <DateTimePicker
                    label="Schedule for Later (Optional)"
                    value={scheduleTime}
                    onChange={(newValue) => setScheduleTime(newValue)}
                    slots={{ textField: (params) => <TextField {...params} fullWidth /> }}
                    disablePast
                    enableAccessibleFieldDOMStructure={false}
                />
            </Box>

            <Box sx={{ mt: 2, border: '1px dashed grey', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>Attachments</Typography>
            {existingAttachments.map((attachment, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, bgcolor: 'grey.100', p: 1, borderRadius: 0.5 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>{attachment.name}</Typography>
                <IconButton size="small" color="error" onClick={() => handleRemoveExistingAttachment(index)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
                </Box>
            ))}
            {selectedFiles.map((file, index) => (
                <Box key={file.name + index} sx={{ display: 'flex', alignItems: 'center', mb: 1, bgcolor: 'primary.light', color: 'primary.contrastText', p: 1, borderRadius: 0.5 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>{file.name}</Typography>
                <IconButton size="small" color="inherit" onClick={() => handleRemoveSelectedFile(index)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
                </Box>
            ))}
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ mt: 1 }}>
                Add Files
                <input type="file" hidden multiple onChange={handleFileChange} />
            </Button>
            </Box>

            {canSeeNotificationCheckbox && (
            <FormControlLabel
                control={
                <Checkbox
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    name="sendNotification"
                />
                }
                label={scheduleTime ? "Send push notification when announcement is published" : "Send push notification for this announcement"}
            />
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">{announcement ? 'Save Changes' : 'Create Announcement'}</Button>
            </Box>
        </Box>
        </Modal>
    </LocalizationProvider>
  );
}

export default AnnouncementModal;
